/**
 * Catálogo da documentação do design system: o que cada seção de
 * /admin/design-system mostra, item por item.
 *
 * É a fonte de três coisas, e por isso elas não divergem:
 *
 * 1. **A busca** (`useBuscaNaDocumentacao.ts`) procura nos itens daqui — não
 *    há índice escrito à parte. Um item novo na tela entra na busca ao entrar
 *    aqui; um item que sai da tela sai da busca junto (`catalogo.spec.tsx`
 *    confere, seção por seção, que todo item tem a sua âncora na tela e toda
 *    âncora da tela tem o seu item).
 * 2. **A âncora** de cada item na tela: o `id` é o `data-search-id` do
 *    elemento que a busca rola até ele (o `id` de `ComponentShowcase`, de
 *    `ItemDaDocumentacao`, de `ColorSwatch`).
 * 3. **A cobertura** (`cobertura.spec.ts`): todo componente que `ui/` exporta
 *    está em `componentes` de algum item, ou em `FORA_DA_DOCUMENTACAO` com
 *    motivo; e todo nome daqui existe em `ui/` — a documentação não descreve
 *    componente que não existe.
 *
 * `componentes` só traz nome exportado por `apps/web/src/ui/` (inclusive os
 * primitivos de `ui/base`). O que mora em `nucleo/` — a casca de navegação, a
 * `RotaProtegida` — entra pelas palavras-chave, porque não é design system.
 */

import { GRUPOS_DA_PALETA, PANTONE } from './paleta';

export interface ItemDoCatalogo {
  /** Âncora do item na tela (`data-search-id`). Único na documentação inteira. */
  id: string;
  /** Como o item aparece na busca. */
  titulo: string;
  /** O que mais alguém digitaria para achá-lo (sinônimo, nome do token, termo do domínio). */
  palavrasChave: readonly string[];
  /** Nomes exportados por `apps/web/src/ui/` que o item documenta. Vazio = item de texto. */
  componentes: readonly string[];
}

export const IDS_DAS_SECOES = [
  'visao-geral',
  'cores',
  'tipografia',
  'espacamento',
  'componentes-base',
  'navegacao',
  'formularios',
  'componentes-sistema',
  'graficos',
  'padroes',
  'acessibilidade',
  'conteudo',
] as const;

export type IdDaSecao = (typeof IDS_DAS_SECOES)[number];

function item(
  id: string,
  titulo: string,
  componentes: readonly string[],
  palavrasChave: readonly string[] = [],
): ItemDoCatalogo {
  return { id, titulo, componentes, palavrasChave };
}

/** Um item por token da paleta: o `ColorSwatch` de cada um é a âncora (`color-<token>`). */
const ITENS_DA_PALETA: readonly ItemDoCatalogo[] = GRUPOS_DA_PALETA.flatMap((grupo) =>
  grupo.entradas.map((entrada) =>
    item(
      `color-${entrada.token}`,
      entrada.nome,
      [],
      [
        `--${entrada.token}`,
        entrada.token,
        grupo.titulo,
        entrada.uso,
        ...(PANTONE[entrada.token] ? [PANTONE[entrada.token]!] : []),
      ],
    ),
  ),
);

export const CATALOGO: Readonly<Record<IdDaSecao, readonly ItemDoCatalogo[]>> = {
  'visao-geral': [
    item(
      'visao-geral-proposito',
      'Para que serve o Design System',
      [],
      ['fonte única', 'identidade visual', 'prefeitura'],
    ),
    item('visao-geral-principios', 'Princípios aplicados', [], ['reuso', 'tokens', 'estados']),
    item(
      'visao-geral-composicao',
      'Composição típica de uma tela',
      [],
      ['CascaDeArea', 'estrutura', 'anatomia'],
    ),
    item(
      'visao-geral-indicadores',
      'Indicadores em componentes reais',
      ['StatsGrid'],
      ['kpi', 'painel'],
    ),
  ],

  cores: [
    ...ITENS_DA_PALETA,
    item(
      'accessibility-checker',
      'Verificador de contraste',
      [],
      ['wcag', 'contraste', 'razão', 'acessibilidade'],
    ),
  ],

  tipografia: [
    item(
      'tipografia-inter',
      'Escala tipográfica (Inter)',
      [],
      ['fonte', 'título', 'corpo', 'h1', 'texto'],
    ),
    item('tipografia-mono', 'JetBrains Mono', [], ['monoespaçada', 'código', 'número', 'valor']),
    item('tipografia-uso', 'Uso recomendado da tipografia', [], ['PageHeader', 'rótulo', 'h1']),
  ],

  espacamento: [
    item('espacamento-escala', 'Escala de espaçamento', [], ['gap', 'padding', 'margem']),
    item('espacamento-arredondamento', 'Arredondamento', [], ['radius', 'borda', 'rounded']),
    item(
      'espacamento-pontos-de-quebra',
      'Pontos de quebra',
      [],
      ['breakpoint', 'responsivo', 'celular', '375'],
    ),
  ],

  'componentes-base': [
    item('button', 'Button', ['Button'], ['botão', 'ação']),
    item('badge', 'Badge', ['Badge'], ['selo', 'etiqueta']),
    item('input', 'Input', ['Input'], ['campo', 'texto']),
    item(
      'select',
      'Select',
      ['Select', 'SelectTrigger', 'SelectContent', 'SelectItem'],
      ['lista suspensa', 'seleção'],
    ),
    item(
      'radio-group',
      'RadioGroup',
      ['RadioGroup', 'RadioGroupItem', 'Label'],
      ['opção', 'rádio', 'rótulo'],
    ),
    item(
      'checkbox-switch',
      'Checkbox e Switch',
      ['Checkbox', 'Switch'],
      ['caixa de seleção', 'interruptor'],
    ),
    item('dialog', 'Dialog', ['Dialog', 'DialogContent', 'DialogTrigger'], ['modal', 'janela']),
    item('card', 'Card', ['Card', 'CardHeader', 'CardTitle', 'CardContent'], ['cartão']),
    item('tabs', 'Tabs', ['Tabs', 'TabsList', 'TabsTrigger', 'TabsContent'], ['abas']),
    item('accordion', 'Accordion', ['Accordion', 'AccordionItem'], ['sanfona', 'recolhível']),
    item('popover', 'Popover', ['Popover', 'PopoverTrigger', 'PopoverContent'], ['balão']),
    item('sheet', 'Sheet', ['Sheet', 'SheetContent'], ['gaveta', 'painel lateral']),
    item('separator', 'Separator', ['Separator'], ['divisor']),
    item('progress', 'Progress', ['Progress'], ['barra de progresso']),
    item('scroll-area', 'ScrollArea', ['ScrollArea'], ['rolagem']),
    item('tooltip', 'Tooltip', ['Tooltip', 'TooltipTrigger', 'TooltipContent'], ['dica']),
    item('primitivo-alert-dialog', 'AlertDialog', ['AlertDialog'], ['confirmação', 'primitivo']),
    item('primitivo-avatar', 'Avatar', ['Avatar', 'AvatarFallback'], ['iniciais', 'retrato']),
    item('primitivo-breadcrumb', 'Breadcrumb', ['Breadcrumb'], ['trilha', 'migalhas']),
    item('primitivo-calendar', 'Calendar', ['Calendar'], ['calendário', 'data']),
    item('primitivo-collapsible', 'Collapsible', ['Collapsible'], ['recolher', 'expandir']),
    item('primitivo-command', 'Command', ['Command'], ['comando', 'busca na lista']),
    item('primitivo-drawer', 'Drawer', ['Drawer'], ['gaveta inferior', 'celular']),
    item('primitivo-dropdown-menu', 'DropdownMenu', ['DropdownMenu'], ['menu', 'ações']),
    item('primitivo-input-otp', 'InputOTP', ['InputOTP'], ['código', 'segundo fator', 'mfa']),
    item('primitivo-skeleton', 'Skeleton', ['Skeleton'], ['esqueleto', 'carregando']),
    item('primitivo-table', 'Table', ['Table'], ['tabela simples']),
    item(
      'primitivo-table-skeleton',
      'TableSkeleton, CardSkeleton e StatCardSkeleton',
      ['TableSkeleton', 'CardSkeleton', 'StatCardSkeleton'],
      ['esqueleto', 'carregando'],
    ),
    item('primitivo-textarea', 'Textarea', ['Textarea'], ['texto longo', 'observação']),
    item('primitivo-toggle', 'Toggle', ['Toggle'], ['alternar']),
    item(
      'primitivo-toggle-group',
      'ToggleGroup',
      ['ToggleGroup', 'ToggleGroupItem'],
      ['grupo de alternância'],
    ),
  ],

  navegacao: [
    item(
      'casca-das-areas',
      'A casca das áreas internas',
      [],
      ['CascaDeArea', 'BarraLateral', 'BuscaGlobal', 'Migalhas', 'MenuDaConta', 'menu lateral'],
    ),
    item(
      'componentes-da-navegacao',
      'Componentes da navegação e do portal',
      ['SkipToContent', 'ThemeToggle', 'MobileNavMenu', 'AcessibilidadeWidget', 'CookieConsent'],
      ['casca', 'cookies', 'acessibilidade', 'nucleo/navegacao'],
    ),
    item('notificacoes', 'Notificacoes (sino)', [], ['sino', 'não lidas', 'GavetaDeNotificacoes']),
    item('theme-toggle', 'ThemeToggle', ['ThemeToggle'], ['tema', 'escuro', 'claro']),
    item('mobile-nav-menu', 'MobileNavMenu', ['MobileNavMenu'], ['celular', 'voltar']),
    item('skip-to-content', 'SkipToContent', ['SkipToContent'], ['pular para o conteúdo', 'foco']),
    item('portal-hero', 'PortalHero', ['PortalHero'], ['portal público', 'capa', 'foto']),
    item('botao-gov-br', 'BotaoGovBr', ['BotaoGovBr'], ['gov.br', 'entrar', 'login']),
    item('lgpd-notice', 'LgpdNotice', ['LgpdNotice'], ['privacidade', 'consentimento', 'lgpd']),
  ],

  formularios: [
    item('form-page', 'FormPage', ['FormPage'], ['formulário', 'salvar', 'cancelar']),
    item('form-section', 'FormSection', ['FormSection'], ['grupo de campos']),
    item(
      'form-field-wrapper',
      'FormFieldWrapper',
      ['FormFieldWrapper'],
      ['campo', 'rótulo', 'obrigatório'],
    ),
    item('form-back-button', 'FormBackButton', ['FormBackButton'], ['voltar']),
    item('field-tooltip', 'FieldTooltip', ['FieldTooltip'], ['ajuda do campo', 'dica']),
    item(
      'aviso-de-rascunho',
      'AvisoDeRascunho',
      ['AvisoDeRascunho'],
      ['rascunho', 'restaurar', 'autosave'],
    ),
    item('money-input', 'MoneyInput', ['MoneyInput'], ['dinheiro', 'real', 'valor', 'moeda']),
    item('month-select', 'MonthSelect', ['MonthSelect'], ['mês', 'competência']),
    item(
      'searchable-select',
      'SearchableSelect',
      ['SearchableSelect'],
      ['combobox', 'busca', 'mais de dez opções'],
    ),
    item(
      'multi-select-combobox',
      'MultiSelectCombobox',
      ['MultiSelectCombobox'],
      ['seleção múltipla', 'combobox'],
    ),
    item(
      'responsive-modal',
      'ResponsiveModal e RodapeDoFormularioEmModal',
      ['ResponsiveModal', 'RodapeDoFormularioEmModal'],
      ['modal', 'gaveta', 'celular', 'formulário em modal'],
    ),
    item(
      'assistente-de-etapas',
      'AssistenteDeEtapas',
      ['AssistenteDeEtapas'],
      ['etapas', 'wizard', 'passo a passo'],
    ),
  ],

  'componentes-sistema': [
    item('data-table', 'DataTable', ['DataTable'], ['tabela', 'listagem', 'linha clicável']),
    item('filter-bar', 'FilterBar', ['FilterBar'], ['filtros', 'busca']),
    item(
      'personalizar-colunas',
      'Personalizar colunas',
      ['TableViewControls'],
      ['colunas', 'personalizacao', 'useTableViewPreferences', 'padrão único'],
    ),
    item('data-pagination', 'DataPagination', ['DataPagination'], ['paginação', 'páginas']),
    item(
      'mobile-card-list',
      'MobileCardList',
      ['MobileCardList'],
      ['cartões', 'celular', '375', 'teclado'],
    ),
    item(
      'paginacao-compacta',
      'PaginacaoCompacta',
      ['PaginacaoCompacta'],
      ['paginação', 'celular'],
    ),
    item('clear-filters-button', 'ClearFiltersButton', ['ClearFiltersButton'], ['limpar filtros']),
    item('filter-presets', 'FilterPresets', ['FilterPresets'], ['atalhos', 'filtro rápido']),
    item(
      'date-range-preset-filter',
      'DateRangePresetFilter',
      ['DateRangePresetFilter'],
      ['período', 'hoje', 'últimos 7 dias', 'resolveDateRange'],
    ),
    item('date-range-filter', 'DateRangeFilter', ['DateRangeFilter'], ['período', 'de até']),
    item('month-filter', 'MonthFilter', ['MonthFilter'], ['competência', 'mês']),
    item('status-badge', 'StatusBadge', ['StatusBadge'], ['situação', 'selo']),
    item(
      'tipo-pessoa-badge',
      'TipoPessoaBadge',
      ['TipoPessoaBadge'],
      ['PF', 'PJ', 'cooperativa', 'natureza'],
    ),
    item('copyable-text', 'CopyableText', ['CopyableText'], ['copiar', 'protocolo']),
    item(
      'copyable-variantes',
      'CopyableCpfCnpj, CopyableTelefone, CopyablePlaca e CopyableEmail',
      ['CopyableCpfCnpj', 'CopyableTelefone', 'CopyablePlaca', 'CopyableEmail'],
      ['copiar', 'cpf', 'cnpj', 'placa', 'telefone', 'e-mail', 'máscara'],
    ),
    item('money-display', 'MoneyDisplay', ['MoneyDisplay'], ['dinheiro', 'real', 'valor']),
    item('empty-value', 'EmptyValue', ['EmptyValue'], ['não informado', 'travessão', 'vazio']),
    item('relative-time', 'RelativeTime', ['RelativeTime'], ['há 5 minutos', 'data relativa']),
    item('updated-at', 'UpdatedAt', ['UpdatedAt'], ['atualizado', 'recarregar']),
    item(
      'destructive-action-chip',
      'DestructiveActionChip',
      ['DestructiveActionChip'],
      ['irreversível', 'atenção'],
    ),
    item('glossary-term', 'GlossaryTerm', ['GlossaryTerm'], ['glossário', 'sigla', 'termo']),
    item('page-header', 'PageHeader', ['PageHeader'], ['título da página', 'h1', 'cabeçalho']),
    item('page-banner', 'PageBanner', ['PageBanner'], ['aviso', 'faixa', 'banner']),
    item('detail-header', 'DetailHeader', ['DetailHeader'], ['detalhe', 'cabeçalho']),
    item('header-info-row', 'HeaderInfoRow', ['HeaderInfoRow'], ['linha de informação']),
    item(
      'detail-summary-sidebar',
      'DetailSummarySidebar',
      ['DetailSummarySidebar'],
      ['resumo', 'auditoria'],
    ),
    item(
      'detail-with-timeline',
      'DetailWithTimeline',
      ['DetailWithTimeline'],
      ['detalhe', 'barra lateral', 'layout'],
    ),
    item('history-timeline', 'HistoryTimeline', ['HistoryTimeline'], ['histórico', 'eventos']),
    item(
      'progress-timeline',
      'ProgressTimeline',
      ['ProgressTimeline'],
      ['linha do tempo', 'andamento'],
    ),
    item(
      'timeline-badge-stepper',
      'TimelineBadgeStepper',
      ['TimelineBadgeStepper'],
      ['etapas', 'siglas'],
    ),
    item(
      'timeline-next-step-banner',
      'TimelineNextStepBanner',
      ['TimelineNextStepBanner'],
      ['próximo passo'],
    ),
    item('next-step-banner', 'NextStepBanner', ['NextStepBanner'], ['próximo passo']),
    item('stat-card', 'StatCard', ['StatCard'], ['indicador', 'kpi']),
    item('stats-grid', 'StatsGrid', ['StatsGrid'], ['indicadores', 'grade']),
    item('confirm-dialog', 'ConfirmDialog', ['ConfirmDialog'], ['confirmação', 'excluir']),
    item('spinner', 'Spinner', ['Spinner'], ['carregando']),
    item('empty-state', 'EmptyState', ['EmptyState'], ['vazio', 'nenhum registro']),
    item(
      'sem-acesso',
      'Sem acesso (ErrorState forbidden)',
      ['ErrorState'],
      ['RotaProtegida', '403', 'acesso negado', 'permissão'],
    ),
    item('teg-logo', 'TegLogo', ['TegLogo'], ['logotipo', 'marca']),
  ],

  graficos: [
    item(
      'cores-de-graficos',
      'Cores de gráficos',
      ['BarGradientDef'],
      ['chart', '--chart-1', 'série', 'recharts'],
    ),
    item('pie-chart-card', 'PieChartCard', ['PieChartCard'], ['pizza', 'rosca', 'gráfico']),
    item('bar-chart-card', 'BarChartCard', ['BarChartCard'], ['barras', 'gráfico']),
    item('dashboard-card', 'DashboardCard', ['DashboardCard'], ['cartão de painel']),
    item('alert', 'Alert', ['Alert', 'AlertTitle', 'AlertDescription'], ['alerta', 'aviso fixo']),
    item(
      'aviso-curto',
      'Aviso curto (sonner)',
      ['Toaster'],
      ['toast', 'sonner', 'mostrarAviso', 'AVISO_DE_ACAO_INDISPONIVEL'],
    ),
    item('notificacao', 'Notificacao', ['Notificacao'], ['aviso', 'sucesso', 'falha', 'Aviso']),
    item(
      'error-state',
      'ErrorState',
      ['ErrorState'],
      ['erro', 'falha', 'não encontrado', 'sessão expirou'],
    ),
    item(
      'estado-da-tela',
      'EstadoDaTela',
      ['EstadoDaTela'],
      ['carregando', '401', '403', 'tentar de novo', 'quatro estados'],
    ),
    item('error-boundary', 'ErrorBoundary', ['ErrorBoundary'], ['quebra', 'erro inesperado']),
    item(
      'referencia-do-tema',
      'Referência do tema dos gráficos',
      [],
      ['chartTheme', 'PIE_COLORS', 'TOOLTIP_STYLE'],
    ),
  ],

  padroes: [
    item('padroes-ordem', 'Ordem obrigatória de uma listagem', [], ['anatomia', 'listagem']),
    item(
      'padroes-linha-clicavel',
      'Linha clicável nas listagens',
      [],
      ['botão ver', 'regra 9', 'teclado'],
    ),
    item('padroes-indicadores-e-filtros', 'Indicadores e filtros', [], ['kpi', 'filtro']),
    item(
      'padroes-regras-transversais',
      'Regras transversais',
      [],
      ['seleção em lote', 'coluna de ações', 'datas', 'valores'],
    ),
  ],

  acessibilidade: [
    item(
      'acessibilidade-requisitos',
      'Requisitos WCAG 2.1 AA',
      [],
      ['wcag', 'contraste', 'toque', 'teclado'],
    ),
    item('acessibilidade-selos', 'Contraste em selos de status', [], ['selo', 'contraste']),
    item('acessibilidade-foco', 'Foco visível e alvo de toque', [], ['foco', '44 px', 'anel']),
  ],

  conteudo: [
    item('conteudo-linguagem', 'Português claro e acionável', [], ['idioma', 'texto']),
    item('conteudo-vocabulario', 'Vocabulário oficial', [], ['termos', 'Ordem de Serviço']),
    item('conteudo-mensagens-de-erro', 'Mensagens de erro', [], ['erro', 'mensagem']),
    item('conteudo-tom', 'Tom por persona', [], ['persona', 'gestor', 'fiscal', 'transportador']),
    item('conteudo-regras', 'Regras de escrita', [], ['botão', 'verbo', 'confirmação']),
  ],
};

/**
 * Componente que `ui/` exporta e a documentação não mostra, com o motivo.
 * Hoje vazia: tudo que `ui/` exporta tem item no catálogo. Quem acrescentar
 * aqui escreve por que o componente não cabe na documentação (mais de 30
 * caracteres) — `cobertura.spec.ts` recusa motivo curto e nome que `ui/` não
 * exporta.
 */
export const FORA_DA_DOCUMENTACAO: Readonly<Record<string, string>> = {};

/**
 * Arquivos de `ui/base` sem amostra própria, com o motivo. Os demais têm
 * item no catálogo (a amostra completa ou a grade "Primitivos").
 */
export const PRIMITIVOS_SEM_SHOWCASE: Readonly<Record<string, string>> = {
  form: 'Ligação do react-hook-form com os campos; quem aparece na tela é o FormFieldWrapper, documentado em Formulários.',
  chart:
    'Contêiner do Recharts com o tema; aparece dentro de PieChartCard e BarChartCard, documentados em Gráficos.',
  sidebar:
    'Peças da barra lateral que só a BarraLateral da casca usa; ela não monta fora da casca (ver a aba Navegação).',
  toast:
    'Toast do Radix, não montado em lugar nenhum: o aviso curto canônico é o sonner (Toaster), documentado em Gráficos.',
};

/** Todos os itens, na ordem das seções. */
export function itensDoCatalogo(): readonly (ItemDoCatalogo & { secao: IdDaSecao })[] {
  return IDS_DAS_SECOES.flatMap((secao) => CATALOGO[secao].map((i) => ({ ...i, secao })));
}
